import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import './AddConstituency.css';

const AddConstituency = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    district: '',
    phase: '',  // NEW: Phase field
    pollDate: '',
    resultDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      // Convert phase to integer before sending
      const submitData = {
        ...formData,
        phase: formData.phase ? parseInt(formData.phase) : null
      };
      
      await api.addConstituency(submitData);
      setMessage({ type: 'success', text: t('constituency.successMessage') || 'Constituency added successfully!' });
      
      // Reset form
      setFormData({
        code: '',
        name: '',
        district: '',
        phase: '',
        pollDate: '',
        resultDate: ''
      });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || t('constituency.errorMessage') || 'Failed to add constituency' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-constituency-container">
      <div className="form-header">
        <h2>
          <i className="fas fa-map-marked-alt"></i>
          {t('constituency.title') || 'Add Constituency'}
        </h2>
        <p>{t('constituency.subtitle') || 'Add a new constituency for Bihar Assembly Election 2025'}</p>
      </div>

      {message && (
        <div className={`alert ${message.type}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          {message.text}
          <button onClick={() => setMessage(null)} className="close-alert">×</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="constituency-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="code">
              <i className="fas fa-hashtag"></i>
              {t('constituency.code') || 'Constituency Code'} *
            </label>
            <input
              id="code"
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder="AC001"
              required
              maxLength={10}
            />
            <small>{t('constituency.codeFormat') || 'Format: AC001, AC002, etc.'}</small>
          </div>

          <div className="form-group">
            <label htmlFor="name">
              <i className="fas fa-landmark"></i>
              {t('constituency.name') || 'Constituency Name'} *
            </label>
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('constituency.namePlaceholder') || 'Enter constituency name'}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="district">
              <i className="fas fa-map"></i>
              {t('constituency.district') || 'District'} *
            </label>
            <input
              id="district"
              type="text"
              name="district"
              value={formData.district}
              onChange={handleChange}
              placeholder={t('constituency.districtPlaceholder') || 'Enter district name'}
              required
            />
          </div>

          {/* NEW: Phase Dropdown */}
          <div className="form-group">
            <label htmlFor="phase">
              <i className="fas fa-layer-group"></i>
              {t('constituency.phase') || 'Election Phase'} *
            </label>
            <select
              id="phase"
              name="phase"
              value={formData.phase}
              onChange={handleChange}
              required
              className="phase-select"
            >
              <option value="">Select Phase...</option>
              <option value="1">Phase 1 - 6 November 2025</option>
              <option value="2">Phase 2 - 11 November 2025</option>
            </select>
            <small>{t('constituency.phaseHelp') || 'Select the voting phase for this constituency'}</small>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="pollDate">
              <i className="fas fa-calendar-day"></i>
              {t('constituency.pollDate') || 'Poll Date'} *
            </label>
            <input
              id="pollDate"
              type="date"
              name="pollDate"
              value={formData.pollDate}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="resultDate">
              <i className="fas fa-calendar-check"></i>
              {t('constituency.resultDate') || 'Result Date'} *
            </label>
            <input
              id="resultDate"
              type="date"
              name="resultDate"
              value={formData.resultDate}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? (
              <>
                <div className="spinner"></div>
                {t('constituency.adding') || 'Adding...'}
              </>
            ) : (
              <>
                <i className="fas fa-plus-circle"></i>
                {t('constituency.addButton') || 'Add Constituency'}
              </>
            )}
          </button>
          <button 
            type="button" 
            onClick={() => setFormData({
              code: '', 
              name: '', 
              district: '', 
              phase: '',
              pollDate: '', 
              resultDate: ''
            })}
            className="reset-btn"
          >
            <i className="fas fa-redo"></i>
            {t('constituency.resetButton') || 'Reset'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddConstituency;
